import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 6rem 2rem;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
      linear-gradient(60deg, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
      linear-gradient(-60deg, rgba(168, 85, 247, 0.05) 0%, transparent 50%);
    opacity: 0.8;
    animation: gradientShift 15s ease infinite;
  }

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FormSection = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 12px rgba(37, 99, 235, 0.1);
    border-color: rgba(37, 99, 235, 0.2);
  }
`;

const FormTitle = styled(motion.h2)`
  font-size: 1.75rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
`;

const FormSubtitle = styled.p`
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #1e293b;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(37, 99, 235, 0.1);
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    background: white;
    transform: translateY(-1px);
  }

  &:hover {
    border-color: rgba(37, 99, 235, 0.3);
  }

  &:valid {
    border-color: rgba(34, 197, 94, 0.3);
  }

  &:invalid:not(:placeholder-shown) {
    border-color: rgba(239, 68, 68, 0.3);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(37, 99, 235, 0.1);
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%232563eb' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.2em;
  padding-right: 3rem;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    background-color: white;
    transform: translateY(-1px);
  }

  &:hover {
    border-color: rgba(37, 99, 235, 0.3);
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  padding: 1rem;
  border-radius: 12px;
  border: none;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(37, 99, 235, 0.2);
  }
`;

const HeroText = styled.div`
  @media (max-width: 968px) {
    text-align: center;
  }
`;

const HeroBadge = styled(motion.span)`
  display: inline-block;
  padding: 0.6rem 1.2rem;
  background: white;
  color: #1e40af;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(37, 99, 235, 0.2);
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.05);
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  color: #1e293b;
  font-size: 1.1rem;

  svg {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: #2563eb;
  }
`;

const SocialProof = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%);
  border-radius: 24px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
  transform: translateY(0);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(37, 99, 235, 0.1);
  }
`;

const CertificationLogos = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  width: 100%;

  .logo-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 1rem;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.05);
    border: 1px solid rgba(37, 99, 235, 0.1);

    &:hover {
      transform: translateY(-5px) scale(1.02);
      background: white;
      box-shadow: 0 8px 16px rgba(37, 99, 235, 0.1);
      border-color: rgba(37, 99, 235, 0.2);
    }

    .logo-text {
      font-size: 1.1rem;
      font-weight: 700;
      background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      transition: all 0.3s ease;
    }

    span {
      font-size: 0.85rem;
      font-weight: 600;
      color: #1e293b;
      transition: color 0.3s ease;
      opacity: 0.8;
    }

    &:hover span {
      color: #2563eb;
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    
    .logo-item {
      min-width: 140px;
    }
  }
`;

const CertificationTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
  position: relative;
  display: inline-block;

  &:after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 32px;
    height: 2px;
    background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
    border-radius: 2px;
  }
`;

function FreeTrial() {
  const [formData, setFormData] = useState({
    storeName: '',
    email: '',
    storeSize: '',
    mainConcern: ''
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('http://localhost:5000/api/free-trial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      setShowSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        jobTitle: '',
        storeName: '',
        storeSize: ''
      });
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    }
  };

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroText>
            <HeroBadge
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              ✨ Free Store Audit
            </HeroBadge>
            <Title
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Unlock Your Store's Full Potential
            </Title>
            <Subtitle>
              Get a comprehensive analysis of your Shopify store's performance, security,
              and user experience. Identify issues before they impact your revenue.
            </Subtitle>
            <BenefitsList>
              <BenefitItem>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                Complete technical analysis
              </BenefitItem>
              <BenefitItem>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                Personalized recommendations
              </BenefitItem>
              <BenefitItem>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                Priority support access
              </BenefitItem>
            </BenefitsList>

            <SocialProof>
              <CertificationTitle>Trusted & Certified Testing Solutions</CertificationTitle>
              <CertificationLogos>
                <div className="logo-item">
                  <div className="logo-text">ISTQB®</div>
                  <span>Certified Tester</span>
                </div>
                <div className="logo-item">
                  <div className="logo-text">ISO 27001</div>
                  <span>Certified</span>
                </div>
                <div className="logo-item">
                  <div className="logo-text">PCI DSS</div>
                  <span>Compliant</span>
                </div>
                <div className="logo-item">
                  <div className="logo-text">SOC 2</div>
                  <span>Type II Certified</span>
                </div>
              </CertificationLogos>
            </SocialProof>
          </HeroText>

          <FormSection
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
            <FormTitle
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >Start Your Free Store Audit</FormTitle>
            <FormSubtitle>
              Get a comprehensive analysis of your store's performance, security, and user experience.
            </FormSubtitle>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">Business Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="jobTitle">Job Title</Label>
                <Input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  placeholder="Store Owner / Manager"
                  value={formData.jobTitle}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="storeName">Shopify Store URL</Label>
                <Input
                  type="text"
                  id="storeName"
                  name="storeName"
                  placeholder="yourstore.myshopify.com"
                  value={formData.storeName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="storeSize">Monthly Orders</Label>
                <Select
                  id="storeSize"
                  name="storeSize"
                  value={formData.storeSize}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select monthly order volume</option>
                  <option value="0-100">0-100 orders</option>
                  <option value="101-1000">101-1,000 orders</option>
                  <option value="1001-10000">1,001-10,000 orders</option>
                  <option value="10001+">10,000+ orders</option>
                </Select>
              </FormGroup>
              <SubmitButton
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Free Audit Report
              </SubmitButton>
            </form>
          </FormSection>
        </HeroContent>
      </Hero>
    </PageContainer>
  );
}

export default FreeTrial;